import { forwardRef, useId, useImperativeHandle, useMemo } from 'react'
import { CREDIT_CARD_BRAND_ORDER } from './creditCardBrandAssets'
import { CREDIT_CARD_MAX_LENGTHS } from './creditCardFormat'
import { resolveCreditCardFormMessages } from './creditCardMessages'
import type {
  CreditCardFormClassNames,
  CreditCardFormHandle,
  CreditCardFormSlots,
  CreditCardMessageTranslator,
  CreditCardType,
} from './creditCardTypes'
import { validateCreditCardCvv } from './creditCardValidation'
import { useCreditCardForm } from './useCreditCardForm'

interface CheckoutCreditCardFormProps {
  translate?: CreditCardMessageTranslator
  slots: CreditCardFormSlots
  classNames: CreditCardFormClassNames
  disabled?: boolean
  hideZip?: boolean
  showPaymentHeader?: boolean
  supportedCardTypes?: readonly CreditCardType[]
}

const CheckoutCreditCardForm = forwardRef<
  CreditCardFormHandle,
  CheckoutCreditCardFormProps
>(function CheckoutCreditCardForm(
  {
    translate,
    slots,
    classNames,
    disabled = false,
    hideZip = false,
    showPaymentHeader = true,
    supportedCardTypes = CREDIT_CARD_BRAND_ORDER,
  },
  ref,
) {
  const headingId = useId()
  const messages = useMemo(
    () => resolveCreditCardFormMessages(translate),
    [translate],
  )
  const form = useCreditCardForm(messages, supportedCardTypes, !hideZip)
  const cardType = form.values.cardType
  const cvvValid =
    form.values.cvv.length > 0 &&
    validateCreditCardCvv(form.values.cvv, cardType, messages) === null
  const cardNumberMaxLength =
    cardType === 'amex'
      ? CREDIT_CARD_MAX_LENGTHS.cardNumberAmex
      : CREDIT_CARD_MAX_LENGTHS.cardNumber
  const cvvMaxLength =
    cardType === 'amex'
      ? CREDIT_CARD_MAX_LENGTHS.cvvAmex
      : CREDIT_CARD_MAX_LENGTHS.cvv

  useImperativeHandle(ref, () => ({
    validateAll: form.actions.validateAll,
    getFormData: form.actions.getFormData,
    reset: form.actions.reset,
  }))

  return (
    <div
      className={classNames.root}
      role="group"
      aria-labelledby={showPaymentHeader ? headingId : undefined}
      aria-label={showPaymentHeader ? undefined : messages.payWith}
    >
      {showPaymentHeader && (
        <div className={classNames.header}>
          <div className={classNames.headingRow}>
            <h3 id={headingId} className={classNames.heading}>
              {messages.payWith}
            </h3>
            {slots.lockIcon}
          </div>
          <p className={classNames.securityNotice}>{messages.securityNotice}</p>
        </div>
      )}

      <div className={classNames.fieldStack}>
        <slots.Field
          inputRef={form.refs.cardNumberRef}
          id={`${headingId}-card-number`}
          label={messages.cardNumberLabel}
          placeholder={messages.cardNumberPlaceholder}
          value={form.values.cardNumber}
          error={form.errors.cardNumber}
          maxLength={cardNumberMaxLength}
          inputMode="numeric"
          autoComplete="cc-number"
          disabled={disabled}
          endAdornment={
            cardType === null
              ? slots.renderCardBrandList(supportedCardTypes)
              : slots.renderCardBrand(cardType)
          }
          onChange={form.actions.setCardNumber}
          onBlur={() => form.actions.onBlur('cardNumber')}
          onFocus={() => form.actions.onFocus('cardNumber')}
        />

        <div className={classNames.fieldRow}>
          <div className={classNames.fieldColumn}>
            <slots.Field
              inputRef={form.refs.expiryRef}
              id={`${headingId}-expiry`}
              label={messages.expiryLabel}
              placeholder={messages.expiryPlaceholder}
              value={form.values.expiry}
              error={form.errors.expiry}
              maxLength={CREDIT_CARD_MAX_LENGTHS.expiry}
              inputMode="numeric"
              autoComplete="cc-exp"
              disabled={disabled}
              endAdornment={null}
              onChange={form.actions.setExpiry}
              onBlur={() => form.actions.onBlur('expiry')}
              onFocus={() => form.actions.onFocus('expiry')}
            />
          </div>
          <div className={classNames.fieldColumn}>
            <slots.Field
              inputRef={form.refs.cvvRef}
              id={`${headingId}-cvv`}
              label={messages.cvvLabel}
              placeholder={messages.cvvPlaceholder}
              value={form.values.cvv}
              error={form.errors.cvv}
              maxLength={cvvMaxLength}
              inputMode="numeric"
              autoComplete="cc-csc"
              disabled={disabled}
              endAdornment={cvvValid ? slots.validIcon : null}
              onChange={form.actions.setCvv}
              onBlur={() => form.actions.onBlur('cvv')}
              onFocus={() => form.actions.onFocus('cvv')}
            />
          </div>
        </div>

        {!hideZip && (
          <slots.Field
            inputRef={form.refs.zipRef}
            id={`${headingId}-zip`}
            label={messages.zipLabel}
            placeholder={messages.zipPlaceholder}
            value={form.values.zip}
            error={form.errors.zip}
            maxLength={CREDIT_CARD_MAX_LENGTHS.zip}
            inputMode="numeric"
            autoComplete="postal-code"
            disabled={disabled}
            endAdornment={null}
            onChange={form.actions.setZip}
            onBlur={() => form.actions.onBlur('zip')}
            onFocus={() => form.actions.onFocus('zip')}
          />
        )}
      </div>
    </div>
  )
})

export default CheckoutCreditCardForm
